import { useEffect, useState } from "react";
import axios from "axios";
import { getAttribution, loadTracking } from "./tracking";
import { RobotCheck, useLeadChallenge } from "./RobotCheck";
import { BusinessFooter } from "./LegalPages";
import { BillingTerms, CycleToggle, OfferCountdown, PriceTag } from "./PriceTag";
import { Check, ChevronRight, CircleDollarSign, MessageCircle, ShieldCheck, Smartphone, Sparkles, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n = 0) => `₹${Number(n).toLocaleString("en-IN")}`;
const api = axios.create({ baseURL: API });

const FEATURES = {
  free: ["Up to 50 students", "Core fee tracking", "Cash / cheque receipts", "English + Hindi UI"],
  basic: ["Up to 500 students", "Receipt PDFs", "Offline collection", "CSV import", "Fee reminders"],
  pro: ["Up to 2,000 students", "Everything in Basic", "Session carry-forward", "Branding & custom color", "Priority support"],
  enterprise: ["Unlimited students", "Everything in Pro", "Multiple branches", "Custom integrations", "Dedicated manager"],
};

export default function LandingPage() {
  const [plans, setPlans] = useState([]); const [offer, setOffer] = useState(null); const [cycle, setCycle] = useState("monthly");
  const [form, setForm] = useState({ school_name: "", contact_name: "", email: "", phone: "", students: "", message: "", plan: "basic" });
  const [done, setDone] = useState(false); const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const ch = useLeadChallenge();
  useEffect(() => { api.get("/public/pricing").then(r => { setPlans(r.data.plans); setOffer(r.data.offer); if (!r.data.offer.monthly_enabled) setCycle("yearly"); }); }, []);
  useEffect(() => { loadTracking(); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/public/leads", { ...form, ...ch.fields, ...getAttribution(), students: form.students ? Number(form.students) : null }); setDone(true); }
    catch (x) { ch.reload(); setErr(x.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  return <div className="landing">
    <header className="landing-nav">
      <div className="landing-brand"><ShieldCheck size={22} /> FeeMitra</div>
      <nav>
        <a href="#features">Features</a>
        <a href="#pricing">Pricing</a>
        <a href="#demo">Book a demo</a>
        <a className="landing-login" href="/" data-testid="landing-login-link">Sign in <ChevronRight size={16} /></a>
      </nav>
    </header>

    <a className="trial-strip" href="/free-trial" data-testid="pricing-free-trial-banner">🎉 14 din FREE trial — koi card nahi. Sab features dekhein aur shuru karein <ChevronRight size={15} /></a>
    <section className="landing-hero">
      <p className="eyebrow">BUILT FOR INDIAN SCHOOLS</p>
      <h1>School fees, finally <span>simple.</span></h1>
      <p className="hero-sub">FeeMitra brings dues, discounts, receipts and reminders into one calm dashboard — in Hindi and English, on any phone.</p>
      <div className="hero-ctas">
        <a className="primary-btn wide" href="/signup" data-testid="hero-signup-cta">Start 14-day free trial <ChevronRight size={17} /></a>
        <a className="secondary-btn" href="#demo" data-testid="hero-demo-cta">Book a free demo</a>
        <a className="secondary-btn" href="/" data-testid="hero-signin-cta">Try the demo workspace</a>
      </div>
      <div className="hero-proof">
        <div><CircleDollarSign size={17} /> UPI, cards, cash & cheque</div>
        <div><MessageCircle size={17} /> WhatsApp reminders</div>
        <div><Smartphone size={17} /> Mobile-first PWA</div>
      </div>
    </section>

    <section className="landing-section" id="features">
      <p className="eyebrow">WHAT YOU GET</p>
      <h2>Everything a fee office needs — in one place</h2>
      <div className="feature-grid">
        {[
          ["Student & class management", "Add students in bulk via CSV, track dues class-wise and promote students session to session."],
          ["Smart fee generation", "Define heads once; monthly, quarterly or annual bills generate idempotently with late-fee rules."],
          ["Verified payments", "Server-verified Razorpay-style flow for UPI, cards, net banking. Cash and cheque recorded with full audit."],
          ["Receipt PDF in one tap", "Every successful payment creates a branded A5 receipt — parents download instantly."],
          ["WhatsApp reminders", "Automatic nudge 3 days before due so collections stay on time without any phone calls."],
          ["Discounts & carry-forward", "Apply sibling or scholarship discounts; safely roll outstanding dues into the next session."],
        ].map(([title, body]) => <div className="feature-card" key={title}><div className="feature-icon"><Sparkles size={16} /></div><strong>{title}</strong><p>{body}</p></div>)}
      </div>
    </section>

    <section className="landing-section" id="pricing">
      <p className="eyebrow">PRICING</p>
      <h2>One price per school · unlimited parents</h2>
      <p className="muted" style={{ maxWidth: 560 }}>Start free, upgrade when you grow. Every plan includes English + Hindi UI, PDF receipts and mobile-first dashboards.</p>
      {offer?.live && <div className="offer-strip" data-testid="pricing-offer-strip">Launch Offer: <b>{offer.discount_pct}% OFF</b> the standard monthly price on all paid plans{offer.ends_at ? ` · till ${offer.ends_at}` : ""}</div>}
      <OfferCountdown offer={offer} testid="pricing-offer-countdown" />
      <CycleToggle cycle={cycle} setCycle={setCycle} offer={offer} />
      <div className="plan-grid">
        {plans.map(p => <div className={`plan-card ${p.key === "pro" ? "featured" : ""}`} key={p.key} data-testid={`plan-${p.key}`}>
          {p.key === "pro" && <span className="plan-tag">Most popular</span>}
          <strong className="plan-name">{p.name}</strong>
          <PriceTag p={p} cycle={cycle} testid={`plan-price-${p.key}`} />
          <ul>{FEATURES[p.key]?.map(f => <li key={f}><Check size={13} /> {f}</li>)}</ul>
          {p.price > 0
            ? <a className="primary-btn wide" href={`/signup?plan=${p.key}`} data-testid={`plan-cta-${p.key}`}>Start free trial <ChevronRight size={15} /></a>
            : <a className="primary-btn wide" href="#demo" onClick={() => setForm(f => ({ ...f, plan: p.key }))} data-testid={`plan-cta-${p.key}`}>Get started <ChevronRight size={15} /></a>}
          <a className="text-btn plan-demo" href="#demo" onClick={() => setForm(f => ({ ...f, plan: p.key }))} data-testid={`plan-demo-${p.key}`}>Book free demo</a>
        </div>)}
      </div>
      <BillingTerms offer={offer} testid="pricing-billing-terms" />
      <div className="pricing-ctas"><a className="primary-btn" href="/signup" data-testid="pricing-start-trial">Start Free Trial <ChevronRight size={15} /></a><a className="secondary-btn" href="#demo" data-testid="pricing-book-demo">Book Free Demo</a></div>
    </section>

    <section className="landing-section demo-section" id="demo">
      <div className="demo-copy">
        <p className="eyebrow">BOOK A DEMO</p>
        <h2>See FeeMitra on your school's data</h2>
        <p className="muted">Share a few details — we'll set up a workspace, import a sample class and walk you through live in 20 minutes.</p>
        <ul className="demo-points">
          <li><Check size={15} /> Zero setup — your school is live the same day</li>
          <li><Check size={15} /> Keep collecting fees even if we're offline</li>
          <li><Check size={15} /> 30-day no-questions refund</li>
        </ul>
      </div>
      {done ? <div className="demo-form done" data-testid="lead-success"><Check size={28} /> <h3>You're on the list!</h3><p>Our team will reach out within 1 business day on <strong>{form.phone}</strong>.</p></div>
       : <form className="demo-form" onSubmit={submit} data-testid="lead-form">
          <label>School name<input required value={form.school_name} onChange={e => setForm({ ...form, school_name: e.target.value })} data-testid="lead-school-input" /></label>
          <label>Your name<input required value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} data-testid="lead-name-input" /></label>
          <label>Email<input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" /></label>
          <label>Phone (WhatsApp)<input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" /></label>
          <div className="form-row">
            <label>Students<input type="number" value={form.students} onChange={e => setForm({ ...form, students: e.target.value })} data-testid="lead-students-input" /></label>
            <label>Plan<select value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })} data-testid="lead-plan-select">{plans.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}</select></label>
          </div>
          <label>What should we show you?<textarea rows="2" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="lead-message-input" /></label>
          <RobotCheck ch={ch} />
          {err && <div className="error-box">{err}</div>}
          <button className="primary-btn wide" disabled={busy} data-testid="lead-submit"><Wallet size={16} /> {busy ? "Sending…" : "Request demo"}</button>
        </form>}
    </section>

    <BusinessFooter />
  </div>;
}
